import { type ReactNode, useEffect, useState } from "react";

import { NoticeLine } from "@styled/notice_line";

import { DraftDocumentView } from "./draft_document_view";
import { TemplateDownload } from "./draft_download";
import { DraftFileMenu } from "./draft_file_menu";
import { DraftTemplateEditView } from "./draft_template_edit_view";
import { DraftTemplateView } from "./draft_template_view";
import { type DraftView, DraftViewControls } from "./draft_view_controls";
import { DraftingChangeLine } from "./drafting_change_line";
import { type AssistantChats } from "./use_assistant_chats";
import { type useDraftSections } from "./use_draft_sections";

type DraftSections = ReturnType<typeof useDraftSections>;

const NO_LAYOUTS: [] = [];

// A Drafting chat's Word draft: the Report | Template switch and Download for
// the pages bar, the Report and his template read-only. Any other draft
// downloads as uploaded, its pages under the server's line.
export const useDraftView = ({
  projectId,
  chats,
  draftSections: {
    draft,
    hasTemplate,
    conversationId,
    edited,
    sections,
    notApplied,
    writingMarker,
    writingLabel,
  },
  line,
  paneHidden,
  setFileId,
}: {
  projectId: string;
  chats: AssistantChats;
  draftSections: DraftSections;
  line: ReactNode;
  paneHidden: boolean;
  setFileId(id: string): void;
}) => {
  const [view, setView] = useState<DraftView>("document");
  const displayDocx = draft?.displayDocx;
  const showDocument = Boolean(displayDocx) && view === "document";
  const showTemplate = Boolean(displayDocx) && view === "template";
  const showTemplateEdit = Boolean(displayDocx) && view === "editTemplate";
  const wordDraftId = displayDocx ? draft?.id : undefined;

  useEffect(() => {
    if (wordDraftId) setFileId(wordDraftId);
  }, [wordDraftId, setFileId]);

  const controls =
    draft && displayDocx ? (
      <DraftViewControls
        projectId={projectId}
        conversationId={conversationId}
        edited={edited}
        templateUrl={draft.file.url}
        view={view}
        onView={setView}
      />
    ) : (
      draft && <TemplateDownload url={draft.file.url} projectId={projectId} />
    );
  const documentView = draft && displayDocx && (
    <>
      <DraftDocumentView
        file={displayDocx}
        sections={sections}
        layouts={draft.draftMarkerLayouts ?? NO_LAYOUTS}
        writing={
          writingMarker === null
            ? null
            : { marker: writingMarker, label: writingLabel }
        }
        notApplied={notApplied}
        filling={Boolean(conversationId) && chats.running}
        line={
          <>
            {line}
            <DraftingChangeLine />
          </>
        }
        hidden={!showDocument || paneHidden}
        projectId={projectId}
        conversationId={conversationId}
      />
      {showTemplateEdit && (
        <DraftTemplateEditView
          projectId={projectId}
          hidden={paneHidden}
          onBack={() => setView("document")}
        />
      )}
    </>
  );

  const templateView = displayDocx && (
    <DraftTemplateView
      projectId={projectId}
      hidden={!showTemplate || paneHidden}
    />
  );

  const fileMenu = draft && displayDocx && (
    <DraftFileMenu
      projectId={projectId}
      name={draft.name}
      onEditTemplate={hasTemplate ? () => setView("editTemplate") : undefined}
    />
  );

  const templateNotice = draft?.draftTemplateNotice;
  const pagesLine = (
    <>
      {line}
      {templateNotice && <NoticeLine>{templateNotice}</NoticeLine>}
      <DraftingChangeLine />
    </>
  );

  return {
    covering: showDocument || showTemplate || showTemplateEdit,
    // His template is being edited: the case's sections and chat wait.
    editingTemplate: showTemplateEdit,
    showReport: () => setView("document"),
    controls,
    fileMenu,
    documentView,
    templateView,
    pagesLine,
  } as const;
};
